import { ActionIcon, Badge, Burger, Group, Image } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../auth/authStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import { UserMenu } from './UserMenu';
import { GlobalSearch } from '../GlobalSearch/GlobalSearch';

interface Props {
  navbarOpened: boolean;
  onNavbarToggle: () => void;
}

interface Branding {
  businessNameAr: string;
  logoUrl: string | null;
}

export function Topbar({ navbarOpened, onNavbarToggle }: Props) {
  const { t } = useTranslation();
  const tenantSlug = useAuthStore((s) => s.tenantSlug);

  const { data: branding } = useQuery({
    queryKey: ['business-branding'] as const,
    queryFn: async () => (await apiClient.get<Branding>('/api/me/business')).data,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="flex h-full items-center justify-between gap-3 border-b border-[var(--wheels-border)] bg-white px-4">
      <Group gap="sm" wrap="nowrap">
        <Burger
          opened={navbarOpened}
          onClick={onNavbarToggle}
          hiddenFrom="sm"
          size="sm"
          aria-label={t('common.menu')}
        />
        {branding?.logoUrl ? (
          <Image src={branding.logoUrl} h={32} w="auto" fit="contain"
            alt={branding.businessNameAr} />
        ) : tenantSlug ? (
          <Badge variant="light" color="gray" size="lg" radius="sm">
            {branding?.businessNameAr ?? tenantSlug}
          </Badge>
        ) : null}
      </Group>

      <Group gap="xs" wrap="nowrap">
        <GlobalSearch />
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={t('common.notifications')}
        >
          <IconBell size={18} stroke={1.6} />
        </ActionIcon>
        <LanguageSwitcher />
        <div className="mx-1 hidden h-6 w-px bg-[var(--wheels-border)] sm:block" />
        <UserMenu />
      </Group>
    </div>
  );
}
